/**
 * Configuración — sacada de Catálogos. Sub-navegación: General · Pasarelas.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, TextField, FormControl, InputLabel, Select, MenuItem,
  CircularProgress, Chip, Switch, FormControlLabel, Tabs, Tab,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import PaymentIcon from '@mui/icons-material/Payment';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import SaveIcon from '@mui/icons-material/Save';
import useToast from './common/useToast';
import ConfigNotificaciones from './ConfigNotificaciones';
import ConfigPlantillasWhatsApp from './ConfigPlantillasWhatsApp';

const API_BASE = process.env.REACT_APP_API_URL || 'https://turismo.nara.chat/api';
const getEmpresaId = () => { try { return JSON.parse(localStorage.getItem('user') || '{}').company_id || '1'; } catch { return '1'; } };
const getAuthHeaders = () => { const t = localStorage.getItem('token'); return { 'Content-Type': 'application/json', ...(t ? { Authorization: `Bearer ${t}` } : {}) }; };

const cardSx = {
  bgcolor: '#fff', borderRadius: 2, border: '1px solid #e2e8f0',
  p: 2.5, boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
};

export default function Configuracion() {
  const [tab, setTab] = useState(0);

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1100, mx: 'auto' }}>
      <Box sx={{ mb: 3 }}>
        <Typography sx={{ fontSize: 22, fontWeight: 800, color: '#134e4a', mb: 0.5 }}>Configuración</Typography>
        <Typography sx={{ fontSize: 13, color: '#64748b' }}>
          Ajustes generales de la operación y pasarelas de pago.
        </Typography>
      </Box>

      <Tabs value={tab} onChange={(_, v) => setTab(v)}
        variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile
        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Tab icon={<SettingsIcon />} iconPosition="start" label="General"
          sx={{ textTransform: 'none', fontWeight: 600 }} />
        <Tab icon={<PaymentIcon />} iconPosition="start" label="Pasarelas de pago"
          sx={{ textTransform: 'none', fontWeight: 600 }} />
        <Tab icon={<NotificationsActiveIcon />} iconPosition="start" label="Notificaciones"
          sx={{ textTransform: 'none', fontWeight: 600 }} />
        <Tab icon={<WhatsAppIcon />} iconPosition="start" label="Plantillas comerciales"
          sx={{ textTransform: 'none', fontWeight: 600 }} />
      </Tabs>

      {tab === 0 && <ConfigGeneral />}
      {tab === 1 && <ConfigPasarelas />}
      {tab === 2 && <ConfigNotificaciones />}
      {tab === 3 && <ConfigPlantillasWhatsApp />}
    </Box>
  );
}

function ConfigGeneral() {
  const empresaId = getEmpresaId();
  const { toast, ToastContainer } = useToast();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/turismo/configuracion/${empresaId}`, { headers: getAuthHeaders() });
      setConfig(await res.json());
    } catch { toast.error('Error al cargar'); }
    setLoading(false);
  }, [empresaId]); // eslint-disable-line
  useEffect(() => { cargar(); }, [cargar]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/turismo/configuracion/${empresaId}`, {
        method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify(config),
      });
      if (!res.ok) { const e = await res.json().catch(()=>({})); throw new Error(e.detail || 'Error'); }
      toast.success('Configuración guardada');
    } catch (e) { toast.error(e.message); }
    setSaving(false);
  };

  if (loading || !config) return <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress size={32} /></Box>;

  return (
    <Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2.5 }}>
        <Box sx={cardSx}>
          <Typography sx={{ fontWeight: 700, fontSize: 14, color: '#134e4a', mb: 2 }}>General</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField size="small" label="Prefijo de reserva" value={config.prefijo_reserva || ''}
              onChange={e => setConfig({ ...config, prefijo_reserva: e.target.value })}
              helperText="Ej: ENT → ENT-20260324-001" />
            <FormControl size="small" fullWidth>
              <InputLabel>Zona horaria</InputLabel>
              <Select label="Zona horaria" value={config.zona_horaria || 'America/Bogota'}
                onChange={e => setConfig({ ...config, zona_horaria: e.target.value })}>
                <MenuItem value="America/Bogota">America/Bogota (Colombia)</MenuItem>
                <MenuItem value="America/Mexico_City">America/Mexico_City (México)</MenuItem>
                <MenuItem value="America/Lima">America/Lima (Perú)</MenuItem>
                <MenuItem value="America/Panama">America/Panama (Panamá)</MenuItem>
                <MenuItem value="America/Santiago">America/Santiago (Chile)</MenuItem>
                <MenuItem value="America/Argentina/Buenos_Aires">America/Buenos_Aires (Argentina)</MenuItem>
                <MenuItem value="America/Cancun">America/Cancún (México Caribe)</MenuItem>
                <MenuItem value="America/New_York">America/New_York (US Este)</MenuItem>
                <MenuItem value="America/Los_Angeles">America/Los_Angeles (US Oeste)</MenuItem>
                <MenuItem value="Europe/Madrid">Europe/Madrid (España)</MenuItem>
              </Select>
            </FormControl>
            <TextField size="small" label="Umbral grupo grande" type="number" value={config.umbral_grupo_grande || 10}
              onChange={e => setConfig({ ...config, umbral_grupo_grande: parseInt(e.target.value) || 10 })}
              helperText="Cantidad de pasajeros para considerar grupo grande" />
            <TextField size="small" label="Cortesía grupo grande" value={config.cortesia_grupo_texto || ''}
              onChange={e => setConfig({ ...config, cortesia_grupo_texto: e.target.value })}
              helperText="Texto que se ofrece a grupos grandes" />
          </Box>
        </Box>

        <Box sx={cardSx}>
          <Typography sx={{ fontWeight: 700, fontSize: 14, color: '#134e4a', mb: 2 }}>Pagos y Reservas</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField size="small" label="Anticipo mínimo (%)" type="number" value={config.anticipo_porcentaje_minimo || 100}
              onChange={e => setConfig({ ...config, anticipo_porcentaje_minimo: parseFloat(e.target.value) || 100 })}
              helperText="100 = pago completo, 50 = 50% de anticipo" />
            <TextField size="small" label="Timeout de HOLD (minutos)" type="number" value={config.hold_timeout_minutos || 30}
              onChange={e => setConfig({ ...config, hold_timeout_minutos: parseInt(e.target.value) || 30 })}
              helperText="Tiempo máximo para mantener una reserva en HOLD" />
            <TextField size="small" label="Hora límite para reservas del día en curso" type="time"
              value={config.hora_cutoff_reservas_dia || '09:00'}
              onChange={e => setConfig({ ...config, hora_cutoff_reservas_dia: e.target.value })}
              InputLabelProps={{ shrink: true }}
              inputProps={{ step: 300 }}
              helperText="Después de esta hora (zona horaria de la empresa) no se aceptan reservas para hoy en wizard, chatbot ni form web" />
            <TextField size="small" label="Métodos de pago" value={(config.metodos_pago || []).join(', ')}
              onChange={e => setConfig({ ...config, metodos_pago: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })}
              helperText="Separados por coma: efectivo, transferencia, nequi" />
            <TextField size="small" label="Mensaje extra en comprobante" multiline rows={2}
              value={config.mensaje_comprobante_extra || ''}
              onChange={e => setConfig({ ...config, mensaje_comprobante_extra: e.target.value })} />
          </Box>
        </Box>
      </Box>

      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" disableElevation startIcon={<SaveIcon />} onClick={handleSave} disabled={saving}
          sx={{ textTransform: 'none', fontWeight: 600,
                background: 'linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)' }}>
          {saving ? 'Guardando...' : 'Guardar configuración'}
        </Button>
      </Box>
      <ToastContainer />
    </Box>
  );
}

function ConfigPasarelas() {
  const empresaId = getEmpresaId();
  const { toast, ToastContainer } = useToast();
  const [pasarelas, setPasarelas] = useState({});
  const [loading, setLoading] = useState(true);
  const [editPasarela, setEditPasarela] = useState(null);
  const [pForm, setPForm] = useState({ activa: false, api_key: '', secret_key: '', webhook_secret: '', sandbox: true });
  const [saving, setSaving] = useState(false);

  const PASARELA_INFO = {
    bold:   { label: 'Bold',   color: '#1a1a2e', desc: 'Tarjeta, PSE, Nequi' },
    wompi:  { label: 'Wompi',  color: '#00c389', desc: 'Bancolombia, PSE, Nequi' },
    epayco: { label: 'ePayco', color: '#ff6b35', desc: 'Tarjeta, PSE, Efecty, Daviplata' },
  };

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/turismo/pagos/pasarelas/${empresaId}`, { headers: getAuthHeaders() });
      if (res.ok) setPasarelas(await res.json());
    } catch { toast.error('Error al cargar pasarelas'); }
    setLoading(false);
  }, [empresaId]); // eslint-disable-line
  useEffect(() => { cargar(); }, [cargar]);

  const handleEdit = (nombre) => {
    const p = pasarelas[nombre] || {};
    setEditPasarela(nombre);
    setPForm({ activa: p.activa || false, api_key: '', secret_key: '', webhook_secret: '', sandbox: p.sandbox !== false });
  };

  const handleSave = async () => {
    if (!editPasarela) return;
    setSaving(true);
    try {
      const body = { ...pForm };
      if (!body.api_key) delete body.api_key;
      if (!body.secret_key) delete body.secret_key;
      if (!body.webhook_secret) delete body.webhook_secret;
      const r = await fetch(`${API_BASE}/turismo/pagos/pasarelas/${empresaId}/${editPasarela}`, {
        method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify(body),
      });
      if (!r.ok) throw new Error('Error');
      toast.success('Pasarela actualizada');
      setEditPasarela(null);
      cargar();
    } catch (e) { toast.error(e.message); }
    setSaving(false);
  };

  if (loading) return <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress size={32} /></Box>;

  return (
    <Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2 }}>
        {Object.entries(PASARELA_INFO).map(([key, info]) => {
          const p = pasarelas[key] || {};
          return (
            <Box key={key} sx={{ ...cardSx, borderLeft: `3px solid ${p.activa ? info.color : '#e2e8f0'}` }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography sx={{ fontWeight: 700, fontSize: 14, color: '#134e4a' }}>{info.label}</Typography>
                <Chip label={p.activa ? 'Activa' : 'Inactiva'} size="small"
                  sx={{ bgcolor: p.activa ? '#10b98115' : '#64748b15', color: p.activa ? '#10b981' : '#64748b', fontWeight: 600, fontSize: 10, height: 20 }} />
              </Box>
              <Typography sx={{ color: '#64748b', fontSize: 11, mb: 1.5 }}>{info.desc}</Typography>
              {p.api_key && <Typography sx={{ color: '#94a3b8', fontSize: 10 }}>API Key: {p.api_key}</Typography>}
              {p.sandbox && <Chip label="Sandbox" size="small" sx={{ height: 16, fontSize: 9, bgcolor: '#f59e0b15', color: '#f59e0b', mt: 0.5 }} />}
              <Button size="small" onClick={() => handleEdit(key)} sx={{ mt: 1, textTransform: 'none', fontSize: 11, color: '#0891b2' }}>
                Configurar →
              </Button>
            </Box>
          );
        })}
      </Box>

      {editPasarela && (
        <Box sx={{ ...cardSx, mt: 3, borderLeft: `3px solid ${PASARELA_INFO[editPasarela]?.color}` }}>
          <Typography sx={{ fontWeight: 700, fontSize: 14, color: '#134e4a', mb: 2 }}>
            Configurar {PASARELA_INFO[editPasarela]?.label}
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
            <TextField size="small" label="API Key" value={pForm.api_key}
              onChange={e => setPForm({ ...pForm, api_key: e.target.value })} placeholder="Dejar vacío para no cambiar" />
            <TextField size="small" label="Secret Key" type="password" value={pForm.secret_key}
              onChange={e => setPForm({ ...pForm, secret_key: e.target.value })} placeholder="Dejar vacío para no cambiar" />
            <TextField size="small" label="Webhook Secret" type="password" value={pForm.webhook_secret}
              onChange={e => setPForm({ ...pForm, webhook_secret: e.target.value })} placeholder="Dejar vacío para no cambiar" />
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <FormControlLabel control={<Switch checked={pForm.sandbox} onChange={e => setPForm({ ...pForm, sandbox: e.target.checked })} />}
                label={<Typography sx={{ fontSize: 13 }}>Sandbox</Typography>} />
              <FormControlLabel control={<Switch checked={pForm.activa} color="success" onChange={e => setPForm({ ...pForm, activa: e.target.checked })} />}
                label={<Typography sx={{ fontSize: 13, fontWeight: 600 }}>Activa</Typography>} />
            </Box>
          </Box>
          <Box sx={{ mt: 2, display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button onClick={() => setEditPasarela(null)} sx={{ textTransform: 'none', color: '#64748b' }}>Cancelar</Button>
            <Button variant="contained" disableElevation onClick={handleSave} disabled={saving}
              sx={{ textTransform: 'none', fontWeight: 600,
                    background: 'linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)' }}>
              {saving ? 'Guardando...' : 'Guardar'}
            </Button>
          </Box>
        </Box>
      )}
      <ToastContainer />
    </Box>
  );
}
